import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { ActivatedRoute, Router, RouterLink, RouterOutlet } from '@angular/router';
import { ICorporateAccount } from '@core/models/admin/account/account.interface';
import { AccountManagementService } from '@core/services/account-management.service';
import { BreadCrumbService } from '@core/services/bread-crumb.service';
import { NotificationFacade } from '@core/facades/notification.facade';
import { ButtonDirective } from '@shared/directives/button.directive';
import { CorporateTabsComponent } from '@shared/ui/corporate-tabs/corporate-tabs.component';
import { DropDownModel, DropdownComponent } from '@shared/ui/dropdown/dropdown.component';
import { BehaviorSubject, forkJoin, Observable, Subscription } from 'rxjs';
import { Clipboard } from '@angular/cdk/clipboard';
import { LoadingService } from '@core/services/loading.service';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { OrderService } from '@core/services/order.service';
import { PortfolioService } from '@core/services/portfolio.service';
import { StatusComponent } from "../../shared/ui/status/status.component";
import { PermissionDirective } from "@shared/directives/permission.directive";
import { RolesAndPermissionsService } from '@core/services/roles-and-permissions.service';

@Component({
  selector: 'app-company-account',
  standalone: true,
  imports: [
    RouterLink,
    RouterOutlet,
    CommonModule,
    CorporateTabsComponent,
    DropdownComponent,
    ButtonDirective,
    NgxSkeletonLoaderModule,
    StatusComponent,
    PermissionDirective
],
  templateUrl: './company-account.component.html',
  styleUrl: './company-account.component.scss',
})
export class CompanyAccountComponent implements OnInit, OnDestroy {
  dropdownVisible = false;
  accountData!: ICorporateAccount | null;
  subs: Subscription[] = [];
  accountId!: string;
  accountNumber!: string;
  totalSignatories = Array.from(Array(3).keys());
  editUrl!: string;
  nairaPortfolioBalance: number = 0;
  dollarPortfolioBalance: number = 0;

  tierLevelSubject$ = new BehaviorSubject<string>('');

  isFetchingTierLevel$!: Observable<boolean>;
  isFetchingAccountInfo$!: Observable<boolean>;
  tierLevel$ = this.tierLevelSubject$.asObservable();

  private notify = inject(NotificationFacade);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private breadCrumbService = inject(BreadCrumbService);
  private accountService = inject(AccountManagementService);
  private change = inject(ChangeDetectorRef);
  private clipboard = inject(Clipboard);
  private loadingService = inject(LoadingService);
  private orderService = inject(OrderService);
  private portfolioService = inject(PortfolioService);
  private permissionService = inject(RolesAndPermissionsService);

  constructor() {
    this.isFetchingAccountInfo$ = this.loadingService.getLoadingObservable(
      'get-corporate-account-kyc'
    );
  }

  toggle() {
    this.dropdownVisible = !this.dropdownVisible;
    this.change.detectChanges();
  }

  ngOnInit(): void {
    this.getAccountInfo();
    this.breadCrumbService.setPageBreadCrumb(this.route);
    this.getPortfolioBalance();
  }

  getAccountInfo(): void {
    const accountId = this.route.snapshot.params['accountId'];
    this.editUrl = `/admin/accounts/corporate/edit/${accountId}`;

    this.setTabAccountId(accountId);
    const subs = this.accountService
      .getCorporateAccountInfo(accountId)
      .subscribe({
        next: (response: any) => {
          this.accountData = response.data.account_kyc as ICorporateAccount;
          this.accountData.owner = response.data.user;
          this.accountNumber = response.data.account_kyc.mango_account_number;

          this.change.detectChanges();
        },
        error: () => {},
      });
    this.subs.push(subs);
  }

  getPortfolioBalance() {
    const sub = forkJoin({
      nairaRequest: this.portfolioService.getPorfolioBalance({
        account_id: this.accountId,
        currency: 'NGN',
      }),
      dollarRequest: this.portfolioService.getPorfolioBalance({
        account_id: this.accountId,
        currency: 'USD',
      }),
    }).subscribe(({ nairaRequest, dollarRequest }) => {
      this.nairaPortfolioBalance = nairaRequest.data.balance;
      this.dollarPortfolioBalance = dollarRequest.data.balance;

      this.change.detectChanges();
    });

    this.subs.push(sub);
  }

  copy(accountNumber: string) {
    this.clipboard.copy(accountNumber);
    this.notify.success('Successfully copied to clipboard');
  }

  goBack() {
    history.back();
  }

  setTabAccountId(accountId: string) {
    this.accountId = accountId;
  }

  combinedActions(): DropDownModel[] {
    return [
      {
        title: 'Edit Company Profile',
        action: () => {
          this.router.navigateByUrl(`/admin/accounts/corporate/edit/${this.accountId}`);
        },
        show: true && !!this.accountId && this.permissionService.hasPermission('get_account_signatory_mandate'),
      },
      {
        title: 'Cash Statement',
        action: () => {
          this.router.navigate(['/admin/accounts/generate-cash-statement'], {
            queryParams: {
              accountId: this.accountId,
              accountType: "corporate",
            },
          });
        },
        show: true && this.permissionService.hasPermission('can_generate_cash_statement'),
      },
      {
        title: 'Export Report',
        show: true && (this.permissionService.hasPermission('export_individual_kyc_data') || this.permissionService.hasPermission('transaction_statement') || this.permissionService.hasPermission('can_view_nav_reports') || this.permissionService.hasPermission('can_view_order_reports')),
        children: [
          {
            title: 'Client Directory',
            action: () => {
            this.router.navigateByUrl(`/admin/accounts/client-directory-report/coperate/${this.accountId}`);
            },
            show: true && this.permissionService.hasPermission('export_individual_kyc_data'),
          },
          {
            title: 'Account Statement',
            action: () => {
              this.router.navigateByUrl(`/admin/accounts/accocunt-statement-report/${this.accountId}`);
            },
            show: true && this.permissionService.hasPermission('transaction_statement'),
          },
          {
            title: 'Nav',
            action: () => {
              this.router.navigateByUrl(`/admin/accounts/nav-report/${this.accountId}`);
            },
            show: true && this.permissionService.hasPermission('can_view_nav_reports'),
          },
          {
            title: 'Order',
            action: () => {
              this.router.navigateByUrl(`/admin/accounts/order-report/${this.accountId}`);
            },
            show: true && this.permissionService.hasPermission('can_view_order_reports'),
          },
        // {
        //   title: 'Audit Log',
        //   action: () => {
        //     this.router.navigateByUrl('/admin/accounts/joint-profile-details');
        //   },
        //   show: true,
        // },
        ]
      }
    ];
  }

    generateVirtualAccount() {
    const sub = this.accountService.generateVirtualAccountNumber(this.accountId).subscribe({
      next: (response) => {
        this.notify.success('Virtual account generation queued successfully, it may take a few moments to reflect');
        this.change.detectChanges();
      },
      error: (error) => {
        this.notify.error(error?.error?.message || 'Failed to generate virtual account');
      },
    });

    this.subs.push(sub);
  }

  ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
  }
}
